// Date shortcut helpers — all dates use America/Mexico_City for consistency.
// México kept no DST since 2023, so UTC-6 is constant for Mexico City.
const TZ = "America/Mexico_City";
const MX_OFFSET = "-06:00";

/** Return today in Mexico City wall clock as "YYYY-MM-DD", independent of browser TZ. */
export function todayInMx() {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const map = Object.fromEntries(parts.map((p) => [p.type, p.value]));
  return `${map.year}-${map.month}-${map.day}`;
}

/** Add `days` to a YYYY-MM-DD string in Mexico wall clock. Handles month/year rollovers. */
function shiftDay(ymd, days) {
  const [y, m, d] = ymd.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + days);
  const yy = dt.getUTCFullYear();
  const mm = String(dt.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(dt.getUTCDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
}

function firstOfMonth(ymd) {
  const [y, m] = ymd.split("-");
  return `${y}-${m}-01`;
}

function firstOfPrevMonth(ymd) {
  const [y, m] = ymd.split("-").map(Number);
  const py = m === 1 ? y - 1 : y;
  const pm = m === 1 ? 12 : m - 1;
  return `${py}-${String(pm).padStart(2, "0")}-01`;
}

function lastOfPrevMonth(ymd) {
  const first = firstOfMonth(ymd);
  return shiftDay(first, -1);
}

function firstOfYear(ymd) {
  return `${ymd.slice(0, 4)}-01-01`;
}

/**
 * Convert a Mexico-local date string ("YYYY-MM-DD") to a UTC ISO instant.
 * `endOfDay=false` → 00:00:00.000 México (start of that day)
 * `endOfDay=true`  → 23:59:59.999 México (end of that day, inclusive)
 * Result is independent of the browser's timezone.
 */
export function mxDateToUtcISO(ymd, endOfDay = false) {
  if (!ymd) return "";
  const time = endOfDay ? "23:59:59.999" : "00:00:00.000";
  return new Date(`${ymd}T${time}${MX_OFFSET}`).toISOString();
}

/** Returns `{key,label,from,to}` for every shortcut, using Mexico wall-clock. */
export function getDateShortcuts() {
  const today = todayInMx();
  return [
    { key: "today", label: "Hoy", from: today, to: today },
    { key: "yesterday", label: "Ayer", from: shiftDay(today, -1), to: shiftDay(today, -1) },
    { key: "7d", label: "7 días", from: shiftDay(today, -6), to: today },
    { key: "30d", label: "30 días", from: shiftDay(today, -29), to: today },
    { key: "month", label: "Este mes", from: firstOfMonth(today), to: today },
    { key: "last_month", label: "Mes pasado", from: firstOfPrevMonth(today), to: lastOfPrevMonth(today) },
    { key: "year", label: "Este año", from: firstOfYear(today), to: today },
  ];
}

/** Match a from/to pair against known shortcuts. */
export function matchShortcut(from, to) {
  const shortcuts = getDateShortcuts();
  for (const s of shortcuts) {
    if (s.from === from && s.to === to) return s.key;
  }
  return "";
}
